import { FormInstance } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { UseMutateFunction } from 'react-query';
import {
  CustomCardWidget,
  UnitPosition,
  Widget,
  WidgetType,
} from 'api/dashboard/widget';
import { PageLoading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import {
  CustomCardFormValues,
  CustomCards,
} from 'components/organisms/dashboard/components/custom-cards';
import {
  fieldLabelSelector,
  useProjectFieldOptions,
} from 'components/organisms/dashboard/components/utils';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import {
  FilterableField,
  GroupedOptions,
} from 'components/organisms/tasks/fields';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { ApiFilterTransformer } from 'components/pages/api-filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import { EditWidgetMutationPayload, getQueryWidgetKey } from 'queries/widgets';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';

interface EditWidgetProps {
  widget: CustomCardWidget;
  form: FormInstance<CustomCardFormValues>;
  transformer: ApiFilterTransformer<GroupedFilter>;
  fields: GroupedOptions<FilterableField>[];
  closeDrawer: () => void;
  editWidget: UseMutateFunction<
    Widget,
    unknown,
    EditWidgetMutationPayload,
    unknown
  >;
  defaultGroup: FilterGroupType;
}

export const EditCustomNumberWidget: FC<EditWidgetProps> = ({
  widget,
  form,
  transformer,
  fields,
  closeDrawer,
  editWidget,
  defaultGroup,
}) => {
  const { groupedProjectNumberFieldOptions, isfieldsLoading } =
    useProjectFieldOptions(WidgetType.CustomCard);
  const { data, isLoading: isCustomFieldsLoading } = useProjectCustomFields();
  const translate = useTranslate();

  const customFields = useMemo(() => {
    return data
      ? data.filter((field) => {
          return field.fieldDataType === FieldTypes.Number && field.customField;
        })
      : [];
  }, [data]);

  const [customCardsFilter, setCustomCardsFilter] = useState<GroupedFilter>(
    transformer.from(widget.simpleSearchFilter),
  );
  const { metaData: widgetData } = widget;
  const updateWidget = (values: CustomCardFormValues) => {
    const { widgetName, prefix, field, ...metaData } = values;

    editWidget(
      {
        widgetId: widget.widgetId,
        widgetName,
        simpleSearchFilter: transformer.to(customCardsFilter),
        metaData: {
          ...widgetData,
          ...metaData,
          field: field.value,
          unitType: prefix ? UnitPosition.Prefix : UnitPosition.Suffix,
        },
      },
      {
        onSuccess: async (res) => {
          await queryClient.invalidateQueries(
            getQueryWidgetKey(widget.widgetId),
          );
          await queryClient.refetchQueries({
            predicate: (query) =>
              query.queryKey[0] === 'resolve-multiple' &&
              query.queryKey[1] === widget.widgetId,
          });
          const message = translate('dashboard.card.update.success.message', {
            widgetName: res.widgetName,
          });
          const title = translate('success');

          toastService({
            title,
            message,
          }).success();
          closeDrawer();
        },
      },
    );
  };

  return (
    <>
      {isfieldsLoading || isCustomFieldsLoading ? (
        <PageLoading />
      ) : (
        <CustomCards
          onFinish={updateWidget}
          form={form}
          groupedFields={fields}
          defaultGroup={defaultGroup}
          transformer={transformer}
          numberFieldOptions={groupedProjectNumberFieldOptions}
          filterFields={customCardsFilter}
          setFilter={setCustomCardsFilter}
          initialValues={{
            widgetName: widget.widgetName,
            unitName: widgetData.unitName || '',
            aggType: widgetData.aggType,
            field: {
              label: translate(
                fieldLabelSelector(widgetData.field, customFields),
              ),
              value: widgetData.field,
            },
            prefix: widgetData.unitType === UnitPosition.Prefix,
          }}
        />
      )}
    </>
  );
};
